import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import apiConfig from '../../../constants/apiConfig';
import { FileOrFolder, useGetAllFiles } from './useGetAllFiles';

export type ExportedFormFile = Extract<FileOrFolder, { type: 'file' }>;

const ICS_FORM_EXPORTS_FOLDER = 'ICS Form Exports';

export const exportedFormFilesKeys = {
  all: ['exportedFormFiles'] as const,
  byIncident: (incidentId?: string) =>
    ['exportedFormFiles', incidentId] as const,
};

/**
 * Fetches files from the "Exported ICS Forms" folder for a single incident
 * (workspace).
 *
 * Leverages the useGetAllFiles hook so we utilize the React Query cache for the
 * list-all response. Then, if the folder exists, fetch its contents.
 */
export function useExportedFormFiles(incidentId?: string) {
  const { data: allFilesResponse } = useGetAllFiles(incidentId);

  const exportedFolderId = useMemo(() => {
    const allItems = allFilesResponse?.dataset ?? [];
    const folder = allItems.find(
      (x): x is Extract<FileOrFolder, { type: 'folder' }> =>
        x.type === 'folder' && x.folder_name === ICS_FORM_EXPORTS_FOLDER
    );
    return folder?.id ?? null;
  }, [allFilesResponse]);

  return useQuery<ExportedFormFile[], unknown>({
    queryKey: exportedFormFilesKeys.byIncident(incidentId),
    enabled: !!incidentId && !!exportedFolderId,
    queryFn: async () => {
      if (!exportedFolderId) return [];

      const folderRes = await axios.get<FileOrFolder[]>(
        apiConfig.apiGateway.filesBase,
        {
          params: {
            workspaceId: incidentId,
            folderId: exportedFolderId,
          },
        }
      );

      const dataset = folderRes.data ?? [];
      const filesOnly = dataset.filter(
        (x): x is ExportedFormFile => x.type === 'file'
      );
      return filesOnly;
    },
  });
}
